import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { UserPlus, Send, Target, FileCheck, Award, CalendarX, UserCheck, ChevronDown, X, ArrowUpRight, ScrollText } from 'lucide-react';
import PendingRegistrations from '../PendingRegistrations';
import RappelEvaluationEntreprise from '../RappelEvaluationEntreprise';
import PrerequisValidation from '../PrerequisValidation';
import EPMSPValidation from '../EPMSPValidation';
import BlocsCompetencesValidation from '../BlocsCompetencesValidation';
import { AbsencesEnAttente } from './AbsencesEnAttente';
import { ReglementsNonSignes, nonSignesApresRelances } from './ReglementsNonSignes';

const longueur = (r, filtre = () => true) => (r.status === 'fulfilled' && Array.isArray(r.value.data) ? r.value.data.filter(filtre).length : 0);

const chargerCompteurs = async () => {
  const mois = new Date().getMonth();
  const periodes = [mois >= 5 && 'fin juillet', mois >= 6 && 'fin août'].filter(Boolean);
  const [insc, prer, epmsp, blocs, ents, rapp, regl] = await Promise.allSettled([
    axios.get('/pending-registrations'), axios.get('/prerequis/all'), axios.get('/epmsp/all'), axios.get('/blocs-competences/all'),
    periodes.length ? axios.get('/entreprises') : Promise.reject(), periodes.length ? axios.get('/satisfaction/rappels-evaluation') : Promise.reject(),
    axios.get('/reglement-interieur/suivi'),
  ]);
  const envoyes = rapp.status === 'fulfilled' ? rapp.value.data || [] : [];
  const entreprises = ents.status === 'fulfilled' ? ents.value.data || [] : [];
  return {
    inscriptions: longueur(insc),
    prerequis: longueur(prer, s => !s.all_validated),
    epmsp: longueur(epmsp, s => !s.all_validated),
    blocs: longueur(blocs, s => !s.diplome_obtenu),
    reglements: regl.status === 'fulfilled' ? nonSignesApresRelances(regl.value.data).length : 0,
    rappels: periodes.length && entreprises.length
      ? entreprises.reduce((n, e) => n + periodes.filter(p => !envoyes.some(r => r.entreprise_id === e.user_id && r.periode === p)).length, 0)
      : null,
  };
};

const Tuile = ({ item, actif, onClick }) => {
  const { Icone, label, count, lien } = item;
  const aTraiter = count > 0;
  const classes = `group relative flex flex-col gap-2 rounded-xl border-2 p-3 sm:p-4 text-left transition-colors duration-200 ${
    actif ? 'border-[#0F172A] bg-[#0F172A] text-white' : aTraiter ? 'border-amber-300 bg-amber-50 hover:border-amber-500' : 'border-slate-200 bg-slate-50 hover:border-slate-300'}`;
  const contenu = (
    <>
      <div className="flex items-center justify-between">
        <Icone className={`h-5 w-5 ${actif ? 'text-[#8DC63F]' : aTraiter ? 'text-amber-600' : 'text-slate-400'}`} />
        {lien ? <ArrowUpRight className="h-4 w-4 opacity-50" /> : <ChevronDown className={`h-4 w-4 opacity-50 transition-transform duration-200 ${actif ? 'rotate-180' : ''}`} />}
      </div>
      <p className={`text-2xl font-display font-bold leading-none ${actif ? 'text-white' : aTraiter ? 'text-[#0F172A]' : 'text-slate-400'}`} data-testid={`a-traiter-${item.id}-count`}>
        {count ?? '…'}
      </p>
      <div>
        <p className={`text-xs sm:text-sm font-medium leading-tight ${actif ? 'text-white' : 'text-slate-700'}`}>{label}</p>
        <p className={`text-[11px] mt-0.5 ${actif ? 'text-white/70' : aTraiter ? 'text-amber-700' : 'text-slate-400'}`}>{aTraiter ? 'À traiter' : 'Rien à traiter'}</p>
      </div>
    </>
  );
  return lien
    ? <Link to={lien} className={classes} data-testid={`a-traiter-${item.id}`}>{contenu}</Link>
    : <button type="button" onClick={onClick} className={classes} data-testid={`a-traiter-${item.id}`} aria-expanded={actif}>{contenu}</button>;
};

// Tuiles « À traiter » : un clic déplie la liste correspondante juste en dessous (une seule à la fois)
export const ATraiter = ({ user, stagiaires, demandesAbsence }) => {
  const [compteurs, setCompteurs] = useState({});
  const [ouvert, setOuvert] = useState(null);
  const panneauRef = useRef(null);
  useEffect(() => {
    if (ouvert) setTimeout(() => panneauRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, [ouvert]);
  const recharger = useCallback(() => chargerCompteurs().then(setCompteurs), []);
  useEffect(() => { recharger(); }, [recharger]);

  const absences = demandesAbsence.filter(d => d.statut === 'en_attente');
  const items = [
    { id: 'inscriptions', label: 'Inscriptions en attente', Icone: UserPlus, count: compteurs.inscriptions, panneau: <PendingRegistrations /> },
    { id: 'attribution', label: 'Stagiaires à valider', Icone: UserCheck, count: stagiaires.filter(s => s.statut_validation === 'en_attente').length, lien: '/gestion-stagiaires' },
    compteurs.rappels !== null && { id: 'rappels', label: 'Rappels évaluation entreprises', Icone: Send, count: compteurs.rappels, panneau: <RappelEvaluationEntreprise /> },
    { id: 'prerequis', label: 'Validation des prérequis', Icone: Target, count: compteurs.prerequis, panneau: <PrerequisValidation user={user} /> },
    { id: 'epmsp', label: 'EPMSP - Épreuves', Icone: FileCheck, count: compteurs.epmsp, panneau: <EPMSPValidation user={user} /> },
    { id: 'blocs', label: 'Blocs de compétences', Icone: Award, count: compteurs.blocs, panneau: <BlocsCompetencesValidation user={user} /> },
    { id: 'absences', label: "Demandes d'absence", Icone: CalendarX, count: absences.length, panneau: <AbsencesEnAttente demandes={absences} stagiaires={stagiaires} /> },
    { id: 'reglements', label: 'Règlement non signé après 3 relances', Icone: ScrollText, count: compteurs.reglements,
      panneau: <ReglementsNonSignes peutEnvoyer={['centre', 'direction'].includes(user?.role)} /> },
  ].filter(Boolean);
  const actif = items.find(i => i.id === ouvert);
  const basculer = (id) => { setOuvert(o => (o === id ? null : id)); recharger(); };

  return (
    <section className="space-y-3" data-testid="a-traiter">
      <h2 className="text-base md:text-lg font-display font-semibold text-[#0F172A]">À traiter</h2>
      <div className={`grid grid-cols-2 sm:grid-cols-4 ${items.length > 7 ? 'lg:grid-cols-4 xl:grid-cols-8' : 'lg:grid-cols-7'} gap-2 sm:gap-3`}>
        {items.map(item => <Tuile key={item.id} item={item} actif={ouvert === item.id} onClick={() => basculer(item.id)} />)}
      </div>
      {actif && (
        <div ref={panneauRef} className="relative scroll-mt-20 min-h-[75vh] sm:min-h-0 animate-in fade-in slide-in-from-top-2 duration-200" data-testid={`a-traiter-panneau-${actif.id}`}>
          <button type="button" onClick={() => basculer(actif.id)} className="absolute -top-1 right-0 z-10 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm border border-slate-200 hover:text-[#0F172A]" data-testid="a-traiter-fermer">
            <X className="h-3.5 w-3.5" /> Fermer
          </button>
          <div className="pt-6">{actif.panneau}</div>
        </div>
      )}
    </section>
  );
};
